import React, { useEffect, useRef, useState } from 'react';
import { useAlert } from './AlertContext';
import { ConfirmationProvider, useConfirmation } from './ConfirmationContext';
import { CustomInput } from './CustomFormElements';
import './EntityBox.css'; // Ensure to add relevant styling for the box and dropdown menu.

const EntityBox = () => {
  const [entities, setEntities] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [isError, setIsError] = useState<boolean>(false);
  const [dropdownOpen, setDropdownOpen] = useState<number | null>(null);
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();
  const dropdownRefs = useRef<(HTMLDivElement | null)[]>([]);

  const handleAddEntity = (): void => {
    const sanitizedValue = inputValue.trim();

    if (sanitizedValue === '') {
      setIsError(true);
      showAlert('fail', 'Input cannot be empty!');
      return;
    }

    if (/[!@#$%^&*(),.?":{}|<>]/.test(sanitizedValue)) {
      setIsError(true);
      showAlert('fail', 'Special characters are not allowed!');
      return;
    }

    setEntities([...entities, sanitizedValue]);
    setInputValue('');
    setIsError(false);
  };

  const handleInputChange = (_id: string, value: string): void => {
    setInputValue(value);
    setIsError(false);
  };

  const handleDeleteEntity = (entityToDelete: string): void => {
    showConfirmation('Are you sure you want to delete this item?', () => {
      try {
        setEntities((prevEntities) => prevEntities.filter((entity) => entity !== entityToDelete));
        showAlert('success', 'Item was successfully deleted!');
      } catch (error) {
        showAlert('fail', 'Failed to delete the item!');
      }
    });
  };

  const toggleDropdown = (index: number): void => {
    setDropdownOpen(dropdownOpen === index ? null : index);
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent): void => {
      if (!dropdownRefs.current.some((ref) => ref?.contains(event.target as Node))) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  const handleDragStart = (event: React.DragEvent<HTMLDivElement>, entity: string): void => {
    const dragImage = document.createElement('div');
    dragImage.textContent = `{${entity}}`;
    dragImage.className = 'drag-image';
    document.body.appendChild(dragImage);

    event.dataTransfer.setDragImage(
      dragImage,
      dragImage.offsetWidth / 2,
      dragImage.offsetHeight / 2,
    );

    event.dataTransfer.setData('text/plain', `{${entity}}`);

    setTimeout(() => {
      document.body.removeChild(dragImage);
    }, 0);
  };

  return (
    <div className="entity-box">
      <div className="input-container">
        <CustomInput
          id="entity-name"
          placeholder="Enter entity name"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAddEntity();
            }
          }}
          type="text"
          className={isError ? 'input-error' : ''}
        />
        <button className="entity-button" onClick={handleAddEntity}>
          Add
        </button>
      </div>

      <div className="entities-list">
        {entities.map((entity, index) => (
          <div
            key={index}
            className="entity-item"
            draggable
            onDragStart={(event) => handleDragStart(event, entity)}
          >
            <span>{entity}</span>
            <button
              className="entity-delete-button"
              onClick={() => handleDeleteEntity(entity)}
            ></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const EntityBoxWrapper = () => (
  <ConfirmationProvider>
    <EntityBox />
  </ConfirmationProvider>
);

export default EntityBoxWrapper;
